import { useState } from 'react';

export interface AiDynamicSectionRubricStaleNoticeProps {
  /** Regenerates the rubric from the section's current goal/topics/instructions/linked questions.
   *  Omit in any context that cannot perform the call (e.g. a read-only preview with no fetching
   *  of its own) — the notice then renders no regenerate/keep actions, just the plain text. */
  onRegenerateRubric?: () => Promise<void>;
  /** Dismisses the notice, telling the caller the researcher chose to keep the rubric as it is. */
  onKeepRubric?: () => void;
}

/** "your checklist may be out of date" — written for a researcher, not an engineer: no "hash
 *  mismatch", no "stale config". */
const RUBRIC_STALE_NOTICE_TEXT =
  'This checklist was written from an earlier version of the goal, probing instructions, or one '
  + "of the linked questions above. Since then, one of those changed — so the checklist may no "
  + 'longer match what you’re actually asking. Take a look below, then decide: regenerate it '
  + 'from the current version, or keep the one you have.';

const RUBRIC_REGENERATE_BUTTON_TEXT = 'Regenerate checklist';
const RUBRIC_REGENERATE_WORKING_TEXT = 'Regenerating…';
const RUBRIC_KEEP_BUTTON_TEXT = 'Keep this checklist';
const RUBRIC_REGENERATE_ERROR_TEXT =
  "Couldn't regenerate the checklist. The one below is unchanged — try again, or keep it as is.";

/** Both actions are equally legitimate — regenerating loses nothing the author wrote (the rubric
 *  is AI-derived), and keeping is right when the underlying edit was cosmetic — so neither button
 *  gets a "dangerous" (red/destructive) treatment. */
const RUBRIC_STALE_ACTION_BUTTON_CLASS =
  'px-3 py-1.5 bg-white border border-amber-300 rounded-md text-sm font-medium text-amber-900 '
  + 'hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed';

/**
 * The "checklist may be out of date" notice, shown above the rubric whenever the section's
 * `rubric_stale` flag is true. Shared verbatim between `AiDynamicSectionSettings` (the edit panel)
 * and `AiDynamicSectionSummary` (the always-on read view) — the researcher's ruling was "we tell
 * him... then he tells [us]", and the read view is where an author actually looks, so this cannot
 * live in only one of the two places. One component, used from both, so the two can never
 * silently drift apart the way two hand-written copies would.
 */
export function AiDynamicSectionRubricStaleNotice({
  onRegenerateRubric,
  onKeepRubric,
}: AiDynamicSectionRubricStaleNoticeProps) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const regenerate = async () => {
    if (!onRegenerateRubric || isRegenerating) return;
    setIsRegenerating(true);
    setError(null);
    try {
      await onRegenerateRubric();
    } catch {
      setError(RUBRIC_REGENERATE_ERROR_TEXT);
    } finally {
      setIsRegenerating(false);
    }
  };

  const keep = () => {
    setError(null);
    onKeepRubric?.();
  };

  return (
    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm text-amber-900">{RUBRIC_STALE_NOTICE_TEXT}</p>
      {(onRegenerateRubric || onKeepRubric) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {onRegenerateRubric && (
            <button
              type="button"
              onClick={regenerate}
              disabled={isRegenerating}
              className={RUBRIC_STALE_ACTION_BUTTON_CLASS}
            >
              {isRegenerating ? RUBRIC_REGENERATE_WORKING_TEXT : RUBRIC_REGENERATE_BUTTON_TEXT}
            </button>
          )}
          {onKeepRubric && (
            <button type="button" onClick={keep} className={RUBRIC_STALE_ACTION_BUTTON_CLASS}>
              {RUBRIC_KEEP_BUTTON_TEXT}
            </button>
          )}
        </div>
      )}
      {error && <p role="alert" className="mt-2 text-xs text-red-700">{error}</p>}
    </div>
  );
}
